import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import MDEditor from "@uiw/react-md-editor";

import Loader from "../../components/loader";
import api from "../../services/api";

export default function View() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState();
  const [values, setValues] = useState({});
  const [content, setContent] = useState("");
  const [businessUnits, setBusinessUnits] = useState([]);
  const saveTimeout = useRef();
  const contentRef = useRef("");

  useEffect(() => {
    fetchContract();
    fetchBusinessUnits();
  }, [id]);

  const fetchBusinessUnits = async () => {
    const { data, ok } = await api.get("/business_unit");
    if (ok && data) setBusinessUnits(data);
  };

  const fetchContract = async () => {
    const { data, ok } = await api.get(`/legal_document/${id}`);
    if (!ok) return toast.error("Contract not found");
    setContract(data);
    setValues({
      name: data.name,
      projectName: data.projectName,
      userName: data.userName,
      budgetName: data.budgetName,
      business_unit_id: data.business_unit_id || "",
    });
    setContent(data.content || "");
    contentRef.current = data.content || "";
  };

  const save = (fields) => {
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      const { data, ok } = await api.put(`/legal_document/${id}`, fields);
      if (!ok) return toast.error("Failed to save");
      setContract(data);
    }, 800);
  };

  const handleChange = (field, value) => {
    const updated = { ...values, [field]: value };
    setValues(updated);
    save({ ...updated, content: contentRef.current });
  };

  const handleBUChange = (buId) => {
    const bu = businessUnits.find((b) => b._id === buId);
    const updated = { ...values, business_unit_id: buId };
    setValues(updated);
    save({ ...updated, business_unit_name: bu?.name || "", content: contentRef.current });
  };

  const handleContentChange = (value) => {
    contentRef.current = value;
    save({ ...values, content: value });
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this contract?")) return;
    const { ok } = await api.remove(`/legal_document/${id}`);
    if (!ok) return toast.error("Failed to delete");
    toast.success("Contract deleted");
    navigate("/legal_document");
  };

  if (!contract) return <Loader size="small" />;

  return (
    <div className="h-full">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigate("/legal_document")} className="text-sm text-blue-600 hover:underline">
          &larr; Back to contracts
        </button>
        <button onClick={handleDelete} className="px-3 py-1 rounded-md text-white bg-red-500 text-sm">
          Delete
        </button>
      </div>

      <div className="flex gap-4 h-[calc(100vh-160px)]">
        <div className="w-4/5 bg-white rounded-lg border border-gray-200 flex flex-col overflow-hidden" data-color-mode="light">
          <MDEditor
            value={content}
            onChange={(val) => handleContentChange(val || "")}
            height="100%"
            preview="live"
            visibleDragbar={false}
          />
        </div>

        <div className="w-1/5 flex flex-col gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Name</label>
            <input
              type="text"
              value={values.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Template</label>
            <div className="text-sm text-gray-600">{contract.templateName || "-"}</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Project</label>
            <input
              type="text"
              value={values.projectName || ""}
              onChange={(e) => handleChange("projectName", e.target.value)}
              placeholder="-"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">User</label>
            <input
              type="text"
              value={values.userName || ""}
              onChange={(e) => handleChange("userName", e.target.value)}
              placeholder="-"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Business Unit</label>
            <select
              value={values.business_unit_id || ""}
              onChange={(e) => handleBUChange(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
              <option value="">-</option>
              {businessUnits.map((bu) => (
                <option key={bu._id} value={bu._id}>{bu.emoji ? `${bu.emoji} ` : ""}{bu.name}</option>
              ))}
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Budget</label>
            <input
              type="text"
              value={values.budgetName || ""}
              onChange={(e) => handleChange("budgetName", e.target.value)}
              placeholder="-"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 text-xs text-gray-500 space-y-1">
            <div>Updated: {new Date(contract.updated_at).toLocaleString()}</div>
            <div>Created: {new Date(contract.created_at).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
